import { FF_CAPTCHA } from 'constants/features';
import { ONE_MINUTE_IN_MILISECONDS } from 'constants/timers';
import { usePeertransferFlags } from 'hooks/usePeertransferFlags/usePeertransferFlags';
import { useEffect, useMemo, useReducer } from 'react';

type CaptchaState = {
  isEnabled?: boolean;
  token: string;
  isReady: boolean;
  countryCode: string;
};

type CaptchaAction =
  | {
      type: 'SET_ENABLED';
      payload?: boolean;
    }
  | {
      type: 'SET_VERIFIED';
      payload: {
        token: string;
      };
    }
  | {
      type: 'SET_READY';
      payload: boolean;
    }
  | {
      type: 'SET_COUNTRY_CODE';
      payload: string;
    };

const reducer = (state: CaptchaState, action: CaptchaAction) => {
  switch (action.type) {
    case 'SET_ENABLED':
      return { ...state, isEnabled: action.payload };
    case 'SET_VERIFIED':
      return {
        ...state,
        token: action.payload.token,
      };
    case 'SET_READY':
      return { ...state, isReady: action.payload };
    case 'SET_COUNTRY_CODE':
      return { ...state, countryCode: action.payload };
    default:
      return state;
  }
};

const initialState: CaptchaState = {
  token: '',
  isReady: false,
  countryCode: '',
};

const useCaptcha = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { data: captchaFlags, isFetched: isFlagsFetched } =
    usePeertransferFlags(FF_CAPTCHA, {
      staleTime: ONE_MINUTE_IN_MILISECONDS,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    });

  const actions = useMemo(
    () => ({
      setVerified: (token: string) =>
        dispatch({ type: 'SET_VERIFIED', payload: { token } }),
      setReady: (ready: boolean) =>
        dispatch({ type: 'SET_READY', payload: ready }),
      setEnabled: (enabled: boolean) =>
        dispatch({ type: 'SET_ENABLED', payload: enabled }),
      setCountryCode: (countryCode: string) =>
        dispatch({ type: 'SET_COUNTRY_CODE', payload: countryCode }),
    }),
    [],
  );

  useEffect(() => {
    if (isFlagsFetched) {
      const isCaptchaEnabled = captchaFlags?.[FF_CAPTCHA];
      actions.setEnabled(!!isCaptchaEnabled);
    }
  }, [isFlagsFetched, captchaFlags, actions.setEnabled]);

  return {
    state: {
      ...state,
      isFlagsFetched,
    },
    actions,
  };
};

export { useCaptcha };
